'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';

import { clamp } from '@/modules/captcha/shared/puzzle-utils';

interface Point {
  x: number;
  y: number;
}

interface UsePuzzleDragOptions {
  /** Where the piece rests before and between attempts. */
  home: Point;
  /** Largest position the piece may reach — scene size minus the piece size. */
  bounds: Point;
  /**
   * Decides the attempt. Return true to accept (the piece stays where `snapTo` puts it), false
   * to reject (it shakes and returns home).
   */
  onDrop: (position: Point) => { accepted: boolean; snapTo?: Point };
}

/** Pixels an arrow key moves the piece. Matches the design source. */
const KEY_STEP = 12;

/** How long the reject shake runs before the piece returns home. */
const SHAKE_MS = 500;

/**
 * Pointer + keyboard dragging for the "put the car somewhere" puzzles (1a, 2c).
 *
 * ## Invariants:
 * - Uses `setPointerCapture`, not document-level listeners. Why: the playfield sits inside a
 *   scrollable bottom sheet, and without capture a drag that leaves the piece's own box stops
 *   tracking mid-gesture — which reads to the customer as the puzzle being broken.
 * - Keyboard operation is not optional. Why: this modal blocks a payment. A challenge that can
 *   only be passed with a pointer would lock keyboard and switch-access users out of checkout
 *   entirely, which is a far worse outcome than the bad score it is guarding against.
 * - Once accepted, every handler is inert. Why: the solve callback fires on acceptance, and a
 *   trailing pointerup or keypress must not run the hit test a second time against a piece that
 *   has already been snapped into place.
 * - Pending shake timers are cleared on unmount. Why: a timer that outlives the puzzle sets state
 *   on an unmounted component; in tests it fired after jsdom was torn down and failed the run.
 */
export function usePuzzleDrag({ home, bounds, onDrop }: UsePuzzleDragOptions) {
  const [position, setPosition] = useState<Point>(home);
  const [dragging, setDragging] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const grabOffset = useRef<Point>({ x: 0, y: 0 });
  const shakeTimers = useRef(new Set<number>());

  useEffect(() => {
    const timers = shakeTimers.current;
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const settle = useCallback(
    (at: Point) => {
      const { accepted: ok, snapTo } = onDrop(at);
      setDragging(false);
      if (ok) {
        setAccepted(true);
        setPosition(snapTo ?? at);
        return;
      }
      setShaking(true);
      const timer = window.setTimeout(() => {
        shakeTimers.current.delete(timer);
        setShaking(false);
        setPosition(home);
      }, SHAKE_MS);
      shakeTimers.current.add(timer);
    },
    [home, onDrop]
  );

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (accepted) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      grabOffset.current = { x: event.clientX - position.x, y: event.clientY - position.y };
      setDragging(true);
    },
    [accepted, position.x, position.y]
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!dragging || accepted) return;
      setPosition({
        x: clamp(event.clientX - grabOffset.current.x, 0, bounds.x),
        y: clamp(event.clientY - grabOffset.current.y, 0, bounds.y),
      });
    },
    [accepted, bounds.x, bounds.y, dragging]
  );

  const onPointerUp = useCallback(() => {
    if (!dragging || accepted) return;
    settle(position);
  }, [accepted, dragging, position, settle]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (accepted) return;
      const nudge = (dx: number, dy: number) => {
        event.preventDefault();
        setPosition((prev) => ({
          x: clamp(prev.x + dx, 0, bounds.x),
          y: clamp(prev.y + dy, 0, bounds.y),
        }));
      };
      if (event.key === 'ArrowLeft') nudge(-KEY_STEP, 0);
      else if (event.key === 'ArrowRight') nudge(KEY_STEP, 0);
      else if (event.key === 'ArrowUp') nudge(0, -KEY_STEP);
      else if (event.key === 'ArrowDown') nudge(0, KEY_STEP);
      else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        settle(position);
      }
    },
    [accepted, bounds.x, bounds.y, position, settle]
  );

  return {
    position,
    dragging,
    shaking,
    accepted,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onKeyDown },
  };
}
